// Line Items — a Line items field shown as a CHECKLIST (definition.view.kind ===
// 'checklist', set in Settings → Fields). The same lines as the table, read as
// checks: how far along they are, a filter, and one row per check with its
// answer as three buttons — the field's own words (Done / Not done / n/a, or
// Kitted / Not kitted / n/a) — and the rest of the line's columns (a comment,
// an owner, …) under it, or BESIDE it on the same row (view.layout 'row', one
// heading row above). view.allDone names a button that answers every open line
// done in one save, after a yes. view.queue: the first open line is IN THE QUEUE
// (the server keeps it there; answering it moves the next one in) — it reads the
// queue's word and has a filter of its own. definition.lockLines: no line is ever
// removed (no remove buttons; the server refuses it too). Every action saves at once
// through the field's own routes, so History, the field's summary and any
// screen that shows the field stay in step:
//   · ticking stamps "ticked on / by" (the server does it — the same from the
//     table, a script or this view); un-ticking clears the stamp
//   · when the view says the status follows, the server moves the work item
//     To Do → In Progress → Done; the new status comes back and the screen's
//     status badge follows (onWorkItemStatus)
// The definition editor's preview draws it too (handlers = null: read-only).

import React, { useEffect, useMemo, useState } from 'react';
import lineItemsApi from '@modules/database/line-items/frontend/api';

const errText = (e) => e?.response?.data?.error || e?.message || 'Something went wrong';
const norm = (s) => String(s ?? '').trim().toLowerCase();
const localDay = () => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`; };
// a worked-out Due date: dd/mm/yyyy
const fmtDmy = (iso) => (/^\d{4}-\d{2}-\d{2}/.test(String(iso || '')) ? `${String(iso).slice(8, 10)}/${String(iso).slice(5, 7)}/${String(iso).slice(0, 4)}` : '—');
const fmtDay = (iso) => {
  if (!/^\d{4}-\d{2}-\d{2}/.test(String(iso || ''))) return String(iso || '');
  const [y, m, d] = String(iso).slice(0, 10).split('-').map(Number);
  return `${d} ${['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'][m - 1]} ${y}`;
};

export function stateOf(view, line) {
  const a = norm(line?.vals?.[view.status]);
  if ((view.done || []).some((x) => norm(x) === a)) return 'done';
  if ((view.na || []).some((x) => norm(x) === a)) return 'na';
  return 'open';
}
// open, and the one in the queue (view.queue)
export const queuedOf = (view, line) => !!view.queue && norm(line?.vals?.[view.status]) === norm(view.queue);
export function progressOf(view, lines) {
  const s = (lines || []).map((l) => stateOf(view, l));
  const done = s.filter((x) => x === 'done').length; const na = s.filter((x) => x === 'na').length;
  const total = s.length; const applicable = total - na;
  return { done, na, total, applicable, pct: applicable > 0 ? done / applicable : (total ? 1 : null) };
}

function Glyph({ kind }) {
  if (kind === 'check') return <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M20 6 9 17l-5-5" /></svg>;
  if (kind === 'minus') return <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" aria-hidden="true"><path d="M5 12h14" /></svg>;
  if (kind === 'up') return <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="m6 15 6-6 6 6" /></svg>;
  if (kind === 'down') return <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="m6 9 6 6 6-6" /></svg>;
  if (kind === 'trash') return <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M3 6h18M8 6V4h8v2M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" /></svg>;
  return <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true"><path d="M12 5v14M5 12h14" /></svg>;
}

// A text / date / number cell that saves when it is left (or on Enter).
function CellInput({ col, value, disabled, onCommit, label }) {
  const [v, setV] = useState(value ?? '');
  useEffect(() => { setV(value ?? ''); }, [value]);
  const type = col.type === 'date' ? 'date' : (col.type === 'number' || col.type === 'money') ? 'number' : 'text';
  const commit = () => { if (String(v ?? '') !== String(value ?? '')) onCommit(v); };
  return (
    <input className="li-cl-input" type={type} step="any" value={v} disabled={disabled} aria-label={label} placeholder={disabled ? '—' : ''}
      onChange={(e) => setV(e.target.value)} onBlur={commit}
      onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); e.currentTarget.blur(); } if (e.key === 'Escape') { setV(value ?? ''); e.currentTarget.blur(); } }} />
  );
}

// ROW layout: a cell that reads as text (it may wrap) and turns into its input on a click —
// a row of inputs would not fit beside the check in a screen's column.
function InlineCell({ col, value, disabled, onCommit, label }) {
  const [edit, setEdit] = useState(false);
  const shown = value == null || value === '' ? '' : String(value);
  if (edit && !disabled) return <span className="li-cl-cell-edit"><CellInputFocus col={col} value={value} label={label} onDone={(v) => { setEdit(false); if (v !== undefined) onCommit(v); }} /></span>;
  return (
    <button type="button" className={`li-cl-cell-text${disabled ? ' is-ro' : ''}`} disabled={disabled} aria-label={`${label}: ${shown || 'empty'}${disabled ? '' : ' — click to change'}`} onClick={() => setEdit(true)}>
      {shown || (disabled ? '—' : <span className="li-cl-cell-empty">—</span>)}
    </button>
  );
}
function CellInputFocus({ col, value, label, onDone }) {
  const [v, setV] = useState(value ?? '');
  const type = col.type === 'date' ? 'date' : (col.type === 'number' || col.type === 'money') ? 'number' : 'text';
  const done = () => onDone(String(v ?? '') !== String(value ?? '') ? v : undefined);
  return (
    <input autoFocus className="li-cl-input" type={type} step="any" value={v} aria-label={label}
      onChange={(e) => setV(e.target.value)} onBlur={done}
      onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); e.currentTarget.blur(); } if (e.key === 'Escape') { onDone(undefined); } }} />
  );
}

// The check's words: a box that grows with them (a long check is never cut off).
function CheckText({ value, label, onCommit }) {
  const ref = React.useRef(null);
  const fit = () => { const el = ref.current; if (el) { el.style.height = 'auto'; el.style.height = `${el.scrollHeight}px`; } };
  React.useLayoutEffect(fit, [value]);
  // its width can change after it is drawn (a row's columns settle, the screen is resized):
  // the box follows, so a long check is never cut off
  React.useEffect(() => {
    const el = ref.current;
    if (!el || typeof ResizeObserver !== 'function') return undefined;
    let w = -1;
    const ro = new ResizeObserver(() => { if (el.clientWidth !== w) { w = el.clientWidth; fit(); } });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  return (
    <textarea ref={ref} rows={1} className="li-cl-text" defaultValue={value} key={value} aria-label={label} onInput={fit}
      onBlur={(e) => { const t = e.target.value.replace(/\s+/g, ' ').trim(); if (t && t !== String(value)) onCommit(t); else { e.target.value = value; fit(); } }}
      onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); e.currentTarget.blur(); } }} />
  );
}

// state: { canEdit, definition, lines } as the field route returns it.
// handlers: null → read-only (the definition editor's preview).
export default function ChecklistView({ issueKey, fieldId, state, onChanged, onWorkItemStatus, options = {}, preview = false }) {
  const def = state.definition;
  const view = def.view;
  const canEdit = !!state.canEdit && !preview;
  const [rows, setRows] = useState(state.lines || []);
  useEffect(() => { setRows(state.lines || []); }, [state.lines]);
  const [filter, setFilter] = useState('all');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [newText, setNewText] = useState('');

  const [confirmAll, setConfirmAll] = useState(false);
  const [adding, setAdding] = useState(false);   // an empty checklist opened to add its first check

  const itemCol = def.columns.find((c) => c.id === view.item);
  const extra = def.columns.filter((c) => c.id !== view.item && c.id !== view.status && c.id !== view.stampDate && c.id !== view.stampUser && c.type !== 'product');
  const g = useMemo(() => progressOf(view, rows), [view, rows]);
  const count = (s) => rows.filter((l) => stateOf(view, l) === s).length;
  const locked = !!def.lockLines;
  const queued = (l) => queuedOf(view, l);
  const shown = rows.map((l, i) => ({ l, i })).filter(({ l }) => filter === 'all' || (filter === 'queue' ? queued(l) : stateOf(view, l) === filter));
  // the buttons and the filter say the field's own answers ("Kitted / Not kitted / n/a")
  const answer = { done: (view.done || ['Done'])[0], open: view.open || 'Not done', na: (view.na || [])[0] };
  const states = [
    { v: 'done', label: answer.done, glyph: 'check' },
    { v: 'open', label: answer.open, glyph: null },
    ...(answer.na ? [{ v: 'na', label: answer.na, glyph: 'minus' }] : []),
  ];
  const rowMode = view.layout === 'row';
  const stampCols = [def.columns.find((c) => c.id === view.stampDate), def.columns.find((c) => c.id === view.stampUser)].filter(Boolean);
  const openCount = count('open');

  // Every action: save, take the new status the server may report, reload.
  const run = async (fn) => {
    setBusy(true); setError(null);
    try {
      const out = await fn();
      if (out?.status && onWorkItemStatus) onWorkItemStatus(out.status);
      if (onChanged) onChanged();
    } catch (e) { setError(errText(e)); setRows(state.lines || []); }
    setBusy(false);
  };
  const save = (line, vals) => {
    const next = { ...line.vals, ...vals };
    setRows((rs) => rs.map((r) => (r.id === line.id ? { ...r, vals: next } : r)));   // at once, then the server's answer
    return run(() => lineItemsApi.updateLine(line.id, { productKey: line.productKey, productLabel: line.productLabel, vals: next, today: localDay() }));
  };
  const setAnswer = (line, s) => { if (stateOf(view, line) !== s) save(line, { [view.status]: answer[s] }); };
  const add = () => {
    const t = newText.trim(); if (!t) return;
    setNewText('');
    run(() => lineItemsApi.addLine(issueKey, fieldId, { vals: { [view.item]: t, [view.status]: answer.open }, today: localDay() }));
  };
  const remove = (line) => run(() => lineItemsApi.deleteLine(line.id));
  const move = (i, dir) => {
    const ids = rows.map((r) => r.id); const j = i + dir;
    if (j < 0 || j >= ids.length) return;
    [ids[i], ids[j]] = [ids[j], ids[i]];
    setRows((rs) => { const c = [...rs]; [c[i], c[j]] = [c[j], c[i]]; return c; });
    run(() => lineItemsApi.reorder(issueKey, fieldId, ids));
  };
  // the view's allDone button: every open line answered done, in one save (after a yes)
  const answerAll = () => { setConfirmAll(false); run(() => lineItemsApi.answerAll(issueKey, fieldId, localDay())); };
  const tone = g.pct === 1 ? 'li-cl-done' : '';

  // one cell of another column of the line: a select, a formula, or an input that saves on leaving
  const extraCell = (c, l, i, withLabel) => {
    const control = c.type === 'option' ? (
      <select className="li-cl-input" value={l.vals?.[c.id] ?? ''} disabled={!canEdit || busy} aria-label={`${c.label} of check ${i + 1}`} onChange={(e) => save(l, { [c.id]: e.target.value })}>
        <option value="">—</option>
        {(options[c.id] || []).map((o) => <option key={o.id || o.value} value={o.value}>{o.value}</option>)}
        {l.vals?.[c.id] && !(options[c.id] || []).some((o) => o.value === l.vals[c.id]) ? <option value={l.vals[c.id]}>{l.vals[c.id]}</option> : null}
      </select>
    ) : c.type === 'formula' ? <span className="li-cl-static">{l.calc?.[c.id] ?? '—'}</span>
      : c.fromDue ? <span className="li-cl-static li-cl-due">{fmtDmy(l.calc?.[c.id])}</span>
      : withLabel ? <CellInput col={c} value={l.vals?.[c.id]} disabled={!canEdit || busy} label={`${c.label} of check ${i + 1}`} onCommit={(v) => save(l, { [c.id]: v })} />
        : <InlineCell col={c} value={l.vals?.[c.id]} disabled={!canEdit || busy} label={`${c.label} of check ${i + 1}`} onCommit={(v) => save(l, { [c.id]: v })} />;
    if (!withLabel) return <span key={c.id} className={`li-cl-cell li-cl-cell-${c.type}`}>{control}</span>;
    return (
      <label key={c.id} className={`li-cl-field${c.type === 'text' ? ' li-cl-wide' : ''}`}>
        <span>{c.label}</span>
        {control}
      </label>
    );
  };
  const seg = (l, s) => {
    const q = queued(l);
    return (
      <span className="li-cl-seg">
        {states.map((x) => {
          const label = x.v === 'open' && q ? view.queue : x.label;
          return (
            <button key={x.v} type="button" disabled={!canEdit || busy} aria-pressed={s === x.v} aria-label={label} className={s === x.v ? `is-on li-cl-on-${x.v === 'open' && q ? 'queue' : x.v}` : ''} onClick={() => setAnswer(l, x.v)}>
              {x.glyph ? <Glyph kind={x.glyph} /> : null}{label}
            </button>
          );
        })}
      </span>
    );
  };
  const tools = (i, l) => (
    <span className="li-cl-tools">
      <button type="button" aria-label="Move up" disabled={busy || i === 0} onClick={() => move(i, -1)}><Glyph kind="up" /></button>
      <button type="button" aria-label="Move down" disabled={busy || i === rows.length - 1} onClick={() => move(i, 1)}><Glyph kind="down" /></button>
      {locked ? null : <button type="button" aria-label="Remove this check" className="li-danger" disabled={busy} onClick={() => remove(l)}><Glyph kind="trash" /></button>}
    </span>
  );
  const stampOf = (l) => [view.stampDate && l.vals?.[view.stampDate] ? fmtDay(l.vals[view.stampDate]) : '', view.stampUser ? l.vals?.[view.stampUser] : ''].filter(Boolean).join(' · ');
  const checkOf = (l, i) => (canEdit
    ? <CheckText value={String(l.vals?.[view.item] ?? '')} label={`${itemCol?.label || 'Check'} ${i + 1}`} onCommit={(t) => save(l, { [view.item]: t })} />
    : <span className="li-cl-text li-cl-static">{l.vals?.[view.item] ?? ''}</span>);
  // ROW layout: every line on ONE row — the check (its "ticked on / by" under it), its other
  // columns as text (a click edits one), the answer; it fits a screen's column (~800px)
  const isNote = (c) => c.type === 'text' && /comment|note|remark/i.test(`${c.id} ${c.label}`);
  const colWidth = (c) => (c.type === 'number' || c.type === 'money' || c.type === 'formula' ? '58px' : c.type === 'date' ? '84px' : c.type === 'option' ? '110px' : isNote(c) ? 'minmax(90px, 1fr)' : 'minmax(84px, 0.7fr)');
  const cols = ['26px', 'minmax(150px, 2fr)', ...extra.map(colWidth), 'max-content'].join(' ');

  // NO LINES: one quiet line, not a whole empty checklist (a screen shared by work items that never
  // get checks — a Work Instruction made outside the production flow — keeps its space); "+ Add a
  // check" opens the box when someone does want one
  if (!rows.length && !adding) {
    return (
      <div className="li-cl li-cl-empty">
        {error ? <div className="li-error">{error}</div> : null}
        <span className="li-muted">No checks on this work item.</span>
        {canEdit ? <button type="button" className="li-cl-empty-add" onClick={() => setAdding(true)}><Glyph kind="plus" />Add a check</button> : null}
      </div>
    );
  }

  return (
    <div className={`li-cl${rowMode ? ' li-cl-rowmode' : ''}`}>
      {error ? <div className="li-error">{error}</div> : null}
      <div className="li-cl-head">
        <span className="li-cl-pct">{g.total ? `${Math.round((g.pct || 0) * 100)}%` : '—'}</span>
        <span className="li-cl-bar"><span className={`li-cl-bar-fill ${tone}`} style={{ width: `${Math.round((g.pct || 0) * 100)}%` }} /></span>
        <span className="li-cl-count">{g.done}/{g.applicable}{g.na ? ` · ${g.na} n/a` : ''}</span>
        {view.allDone && canEdit && openCount ? (
          <button type="button" className="li-btn li-btn-primary li-cl-all" disabled={busy} onClick={() => setConfirmAll(true)}><Glyph kind="check" />{view.allDone}</button>
        ) : null}
      </div>
      {confirmAll ? (
        <div className="li-cl-confirm" role="alertdialog" aria-label={view.allDone}>
          <span>{openCount === 1 ? 'The one line still open' : `All ${openCount} lines still open`} become <b>{answer.done}</b>{stampCols.length ? ', stamped with today and your name' : ''}.{g.na ? ` The ${g.na} n/a line${g.na === 1 ? ' stays' : 's stay'} n/a.` : ''}</span>
          <button type="button" className="li-btn li-btn-primary" disabled={busy} onClick={answerAll}>{view.allDone}</button>
          <button type="button" className="li-btn" disabled={busy} onClick={() => setConfirmAll(false)}>Cancel</button>
        </div>
      ) : null}
      <div className="li-cl-filter" role="radiogroup" aria-label="Show">
        {[['all', `All ${rows.length}`], ...(view.queue ? [['queue', `${view.queue} ${rows.filter(queued).length}`]] : []), ['open', `${answer.open} ${openCount}`], ['done', `${answer.done} ${count('done')}`], ...(answer.na ? [['na', `${answer.na} ${count('na')}`]] : [])].map(([v, label]) => (
          <button key={v} type="button" role="radio" aria-checked={filter === v} className={filter === v ? 'is-on' : ''} onClick={() => setFilter(v)}>{label}</button>
        ))}
      </div>
      {!rows.length ? <div className="li-muted li-pad">No checks yet.{canEdit ? ' Add the first one below.' : ''}</div> : null}
      {rows.length && rowMode ? (
        <div className="li-cl-list li-cl-rows" style={{ '--li-cl-cols': cols }}>
          <div className="li-cl-grid li-cl-headrow" aria-hidden="true">
            <span />
            <span>{itemCol?.label || 'Check'}{stampCols.length ? <span className="li-cl-head-sub"> · {stampCols.map((c) => c.label.toLowerCase()).join(' · ')}</span> : null}</span>
            {extra.map((c) => <span key={c.id} className={c.type === 'number' || c.type === 'money' || c.type === 'formula' ? 'li-a-right' : ''}>{c.label}</span>)}
            <span>{(def.columns.find((c) => c.id === view.status) || {}).label || 'Answer'}</span>
          </div>
          {shown.map(({ l, i }) => {
            const s = stateOf(view, l);
            const stamp = stampOf(l);
            return (
              <div key={l.id ?? i} className={`li-cl-grid li-cl-row li-cl-${s}${queued(l) ? ' li-cl-queue' : ''}`}>
                <span className="li-cl-n">
                  <span className="li-cl-n-num">{i + 1}</span>
                  {canEdit && !locked ? <button type="button" className="li-cl-n-del" aria-label={`Remove check ${i + 1}`} disabled={busy} onClick={() => remove(l)}><Glyph kind="trash" /></button> : null}
                </span>
                <span className="li-cl-check">
                  {checkOf(l, i)}
                  {stamp ? <span className="li-cl-stamp"><Glyph kind="check" />{stamp}</span> : null}
                </span>
                {extra.map((c) => extraCell(c, l, i, false))}
                {seg(l, s)}
              </div>
            );
          })}
        </div>
      ) : null}
      {rows.length && !rowMode ? (
        <div className="li-cl-list">
          {shown.map(({ l, i }) => {
            const s = stateOf(view, l);
            const stamp = stampOf(l);
            return (
              <div key={l.id ?? i} className={`li-cl-row li-cl-${s}${queued(l) ? ' li-cl-queue' : ''}`}>
                <div className="li-cl-line">
                  <span className="li-cl-n">{i + 1}</span>
                  {checkOf(l, i)}
                  {seg(l, s)}
                </div>
                {(stamp || extra.length || canEdit) ? (
                  <div className="li-cl-meta">
                    {stamp ? <span className="li-cl-stamp"><Glyph kind="check" />{stamp}</span> : null}
                    {extra.map((c) => extraCell(c, l, i, true))}
                    {canEdit ? tools(i, l) : null}
                  </div>
                ) : null}
              </div>
            );
          })}
        </div>
      ) : null}
      {canEdit ? (
        <div className="li-cl-add">
          <Glyph kind="plus" />
          <input value={newText} onChange={(e) => setNewText(e.target.value)} placeholder="Add a check and press Enter" disabled={busy}
            onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); add(); } }} />
        </div>
      ) : null}
    </div>
  );
}
